import type { GbTier } from "@/lib/pricing-utils";

export const PRICING_VERIFIED_DATE = "July 12, 2026";

export const IPROYAL_URL = "https://iproyal.com";
export const IPROYAL_MOBILE_URL = "https://iproyal.com/mobile-proxies/";
export const IPROYAL_RESIDENTIAL_URL = "https://iproyal.com/residential-proxies/";

export type ProxyProduct = "mobile" | "residential";

export interface IproyalPlan {
  name: string;
  gb: number;
  monthlyPrice: number;
  perGb: number;
}

// Rotating mobile bandwidth — volume-tiered per-GB rate, published on IPRoyal's
// mobile proxies page.
export const IPROYAL_MOBILE_PLANS: IproyalPlan[] = [
  { name: "2 GB", gb: 2, monthlyPrice: 13.6, perGb: 6.8 },
  { name: "10 GB", gb: 10, monthlyPrice: 60, perGb: 6.0 },
  { name: "50 GB", gb: 50, monthlyPrice: 280, perGb: 5.6 },
  { name: "100 GB", gb: 100, monthlyPrice: 520, perGb: 5.2 },
];

// Residential subscription pricing tiers from IPRoyal's residential pricing page.
// IPRoyal also offers pay-as-you-go billing at a higher per-GB rate — see
// IPROYAL_RESIDENTIAL_PAYG_NOTE below.
export const IPROYAL_RESIDENTIAL_PLANS: IproyalPlan[] = [
  { name: "1 GB", gb: 1, monthlyPrice: 7.0, perGb: 7.0 },
  { name: "2 GB", gb: 2, monthlyPrice: 11.9, perGb: 5.95 },
  { name: "10 GB", gb: 10, monthlyPrice: 52.5, perGb: 5.25 },
  { name: "50 GB", gb: 50, monthlyPrice: 245, perGb: 4.9 },
];

// IPRoyal's pay-as-you-go residential rate, shown for context — the subscription
// tiers above are the ones we compare against since they're the cheaper option.
export const IPROYAL_RESIDENTIAL_PAYG_NOTE =
  "IPRoyal's pay-as-you-go residential pricing runs about 5% higher than the subscription tiers above: $7.35/GB at 1 GB, $6.25/GB at 2 GB, $5.51/GB at 10 GB, and $5.15/GB at 50 GB.";

export interface IproyalPerProxyPlan {
  name: string;
  monthlyEquivalent: number;
}

export const IPROYAL_PER_PROXY_MOBILE_PLANS: IproyalPerProxyPlan[] = [
  { name: "24 hours", monthlyEquivalent: 10.11 * 30 },
  { name: "30 days", monthlyEquivalent: 130 },
  { name: "60 days", monthlyEquivalent: 123.5 },
  { name: "90 days", monthlyEquivalent: 117 },
];

export const PROXIDIZE_MOBILE_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 2, totalPrice: 50 },
  { gb: 50, monthlyPPG: 2, totalPrice: 100 },
  { gb: 100, monthlyPPG: 2, totalPrice: 200 },
  { gb: 200, monthlyPPG: 2, totalPrice: 400 },
  { gb: 500, monthlyPPG: 2, totalPrice: 1000 },
  { gb: 1000, monthlyPPG: 2, totalPrice: 2000 },
  { gb: 2000, monthlyPPG: 2, totalPrice: 4000 },
  { gb: 5000, monthlyPPG: 2, totalPrice: 10000 },
  { gb: 10000, monthlyPPG: 2, totalPrice: 20000 },
];

export const PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 1, totalPrice: 25 },
  { gb: 50, monthlyPPG: 1, totalPrice: 50 },
  { gb: 100, monthlyPPG: 1, totalPrice: 100 },
  { gb: 200, monthlyPPG: 1, totalPrice: 200 },
  { gb: 500, monthlyPPG: 1, totalPrice: 500 },
  { gb: 1000, monthlyPPG: 1, totalPrice: 1000 },
  { gb: 2000, monthlyPPG: 0.95, totalPrice: 1900 },
  { gb: 5000, monthlyPPG: 0.95, totalPrice: 4750 },
  { gb: 10000, monthlyPPG: 0.9, totalPrice: 9000 },
];

export interface IproyalCost {
  cost: number;
  planLabel: string;
  extrapolated: boolean;
}

export function iproyalMonthlyCost(product: ProxyProduct, gb: number): IproyalCost {
  const plans = product === "mobile" ? IPROYAL_MOBILE_PLANS : IPROYAL_RESIDENTIAL_PLANS;
  const plan = plans.find((p) => p.gb >= gb);
  if (plan)
    return {
      cost: plan.monthlyPrice,
      planLabel: `${plan.name} plan`,
      extrapolated: false,
    };
  const top = plans[plans.length - 1];
  const cost = Math.round(gb * top.perGb * 100) / 100;
  return { cost, planLabel: `${top.name}+ tier rate`, extrapolated: true };
}
